import React from 'react';
import { BadgeType } from 'src/model/componentTypes';
import { ReactComponent as ActiveIcon } from 'src/assets/icons/fi-rr-check.svg';
import { ReactComponent as InactiveIcon } from 'src/assets/icons/pause.svg';
import { ReactComponent as ArchivedIcon } from 'src/assets/icons/fi-rr-folder-unarchive.svg';
import { ReactComponent as DraftIcon } from 'src/assets/icons/fi-rr-pencil.svg';

export function findWorkflowByEventType(workflows: any[], eventType: string) {
  return workflows.find((workflow) => workflow.eventType === eventType);
}
export interface Playbook {
  primaryKey: number;
  playbookName: string;
  filters: any[];
  campaignManager: string | null;
  lastRun: string;
  runs: string | number;
  lastUpdated: string;
  dateCreated: string;
  createdBy: string;
  sequenceName: string | null;
  pushToSequence: boolean;
  status: 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DRAFT';
  description?: string;
  note?: string;
  sequenceId?: number | null;
  mailboxEmail?: string | null;
  mailboxId?: string | null;
  mailboxUserId?: string | null;
  customerListId?: number;
  sortOrder?: number;
  priority?: number;
  userEmails?: string[];
  playbookTemplate: {
    primaryKey: number;
    templateName: string;
    description: string;
    eventType: string;
    entityType: string;
    accountIcpDefault: string;
    personIcpDefault: string;
  } | null;
  contactOwners: {
    primaryKey: number;
    lastAssignmentCount: number;
    user: {
      id: number;
      firstName: string;
      lastName: string;
      emailId: string;
      linkedinUrl: string | null;
      alternateEmailId: string | null;
    };
  }[];
}

export const statusColors: Record<Playbook['status'], BadgeType> = {
  ACTIVE: 'success',
  INACTIVE: 'error',
  ARCHIVED: 'white',
  DRAFT: 'white',
};

export const statusIcons: Record<Playbook['status'], React.ComponentType<any>> = {
  ACTIVE: ActiveIcon,
  INACTIVE: InactiveIcon,
  ARCHIVED: ArchivedIcon,
  DRAFT: DraftIcon,
};

export interface DrawerFormValues {
  playbookName: string;
  playbookStatus: string;
  campaignManager: string;
  sequenceName: string;
  pushToSequence: boolean;
  formError: boolean;
  sequenceId?: number | null;
  mailboxEmail?: string;
  mailboxId?: string;
  mailboxUserId?: string;
}

export function extractEmailIds(data: any[]): string[] {
  return data.map((item) => item.user.emailId);
}

export function transformContactOwners(contactOwners: Playbook['contactOwners']) {
  return contactOwners.map((contact) => {
    const { firstName, lastName, emailId } = contact.user;

    // Handle cases where firstName or lastName might be empty or null
    const firstNameValue = firstName || '';
    const lastNameValue = lastName || '';

    // Create the label by combining firstName and lastName with a space in between
    // Trim to handle cases where one might be empty
    const label = `${firstNameValue} ${lastNameValue}`.trim();

    return {
      label: label || emailId, // If both firstName and lastName are empty, use email as label
      value: emailId,
    };
  });
}

export const templatesForAssignContactToUser = [
  'WAL_ACCOUNT_REVEAL',
  'LEAD_ADDED_TO_TAL',
  'AI Signal is enriched',
];
export const templateForSourceContact = [
  'AI Signal is enriched',
  'Website visits',
  'Account added to TAL',
];

export const featuredCrmIntegration = {
  zoho: 'Zoho',
  salesforce: 'Salesforce',
  hubspot: 'Hubspot',
  // 'FreshSales Integration': 'FreshSales',
  pipedrive: 'Pipedrive',
};

export const templateVerbiage = {
  'Website visits from ICP accounts': 'ICP account visit detected',
  'Website visits >3/5/7/10': 'Visit threshold exceeded (3/5/7/10+)',
  'Losing Ground To Key Competitors': 'Competitor Threat Detected',
  'New Offerings': 'New Product Launch Detected',
  'AI News Insights': 'AI Activity Reported',
  'Competitors News Updates': 'Competitor News Tracked',
  'Recent Funding Announcements': 'Funding Activity Announced',
  'Tech Signals': 'Tech Stack Match Detected',
  'Intent Signals Targeting': 'Intent Criteria Met',
  'Contacts of specific criteria from CRM to CDP': 'CRM-Sourced Contact Added to TAL',
  'Identifying TechStack for most website visited prospect': 'Prospect tech stack identified',
  'Inbound account website visit agent': 'Website visit account is revealed',
  'Lead is added to Target Profiles': 'Lead is added to TAL',
  'Contact is added to Target Profile': 'Contact is added to TAL',
};

// export const featureBasedTemplates = {
//   'Website visits from ICP accounts': ['Website Tracking'],
//   'Identifying TechStack for most website visited prospect': [
//     'Website Tracking',
//     'Contact Reveal',
//     'Custom AI Signals',
//   ],
//   'Contacts of specific criteria from CRM to CDP': [],
//   'Recent Funding Announcements': ['Custom AI Signals'],
//   'Website visits >3/5/7/10': ['Website Tracking'],
//   'AI News Insights': ['Custom AI Signals'],
//   'Competitor News Updates': ['Custom AI Signals'],
//   'New Offerings': ['Custom AI Signals'],
//   'Losing Ground To Key Competitors': ['Custom AI Signals'],
//   'Inbound account website visit agent': ['Website Tracking'],
//   'Lead is added to Target Profiles': [],
//   'Tech Signals': ['Custom AI Signals'],
// };
